/**
 * QuizHistoryContext
 *
 * React context for quiz/exam history state management.
 * Integrates with QuizHistoryOrchestrator for database-backed history
 * with localStorage fallback for unauthenticated users.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import QuizHistoryOrchestrator from '../services/quizHistory/QuizHistoryOrchestrator';

const QuizHistoryContext = createContext(null);

export const QuizHistoryProvider = ({ children }) => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth0();

  const [attempts, setAttempts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Get orchestrator instance
  const orchestrator = useMemo(() => QuizHistoryOrchestrator.getInstance(), []);

  // Initialize orchestrator when auth state is ready
  useEffect(() => {
    if (authLoading) return;

    const initialize = async () => {
      setIsLoading(true);

      const userId = isAuthenticated ? user?.sub : null;
      await orchestrator.initialize(isAuthenticated, userId);

      const state = orchestrator.getState();
      setAttempts(state.attempts);
      setIsInitialized(true);
      setIsLoading(false);
    };

    initialize();
  }, [authLoading, isAuthenticated, user?.sub, orchestrator]);

  // Handle auth state changes (login/logout)
  useEffect(() => {
    if (!isInitialized || authLoading) return;

    const handleAuthChange = async () => {
      const userId = isAuthenticated ? user?.sub : null;
      await orchestrator.switchStorageMode(isAuthenticated, userId);
    };

    handleAuthChange();
  }, [isAuthenticated, user?.sub, isInitialized, authLoading, orchestrator]);

  // Subscribe to orchestrator state changes
  useEffect(() => {
    const unsubscribe = orchestrator.subscribe((state) => {
      setAttempts(state.attempts);
      setIsLoading(state.isLoading);
    });

    return unsubscribe;
  }, [orchestrator]);

  // Save a new attempt
  const saveAttempt = useCallback(async (attemptData) => {
    return await orchestrator.saveAttempt(attemptData);
  }, [orchestrator]);

  // Get all attempts
  const getAllAttempts = useCallback(() => {
    return orchestrator.getAllAttempts();
  }, [orchestrator]);

  // Get attempts by type (quiz or exam)
  const getAttemptsByType = useCallback((contentType) => {
    return orchestrator.getAttemptsByType(contentType);
  }, [orchestrator]);

  // Get attempts for specific content
  const getAttemptsByContent = useCallback((contentType, contentId) => {
    return orchestrator.getAttemptsByContent(contentType, contentId);
  }, [orchestrator]);

  // Get latest attempt for specific content
  const getLatestAttempt = useCallback((contentType, contentId) => {
    return orchestrator.getLatestAttempt(contentType, contentId);
  }, [orchestrator]);

  // Get history as a map (keyed by content_id)
  const getHistoryMap = useCallback((contentType = null) => {
    return orchestrator.getHistoryMap(contentType);
  }, [orchestrator]);

  // Get statistics
  const getStats = useCallback(() => {
    return orchestrator.getStats();
  }, [orchestrator]);

  const value = useMemo(() => ({
    // State
    attempts,
    count: attempts.length,
    isLoading,
    isInitialized,

    // Methods
    saveAttempt,
    getAllAttempts,
    getAttemptsByType,
    getAttemptsByContent,
    getLatestAttempt,
    getHistoryMap,
    getStats
  }), [
    attempts,
    isLoading,
    isInitialized,
    saveAttempt,
    getAllAttempts,
    getAttemptsByType,
    getAttemptsByContent,
    getLatestAttempt,
    getHistoryMap,
    getStats
  ]);

  return (
    <QuizHistoryContext.Provider value={value}>
      {children}
    </QuizHistoryContext.Provider>
  );
};

export const useQuizHistory = () => {
  const context = useContext(QuizHistoryContext);

  if (!context) {
    throw new Error('useQuizHistory must be used within a QuizHistoryProvider');
  }

  return context;
};

export default QuizHistoryContext;
